<template>
	<div class="hris-page">
		<div class="hris-page-inner">
			<div class="hris-page-header">
				<span class="hris-page-eyebrow">System Settings</span>
				<h1 class="hris-page-title">Company</h1>
				<p class="hris-page-subtitle">Manage companies, their tenant/subsidiary structure, and branding logo.</p>
			</div>

			<q-form ref="vForm" @submit="onSubmit">
				<div class="row q-col-gutter-lg">
					<div class="col-12 col-md-4">
						<div class="hris-form-section">
							<h2 class="hris-form-section-title">Add / Edit Company</h2>
							<div class="hris-form-row">
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="company" label="Company" :rules="[formRules.required]" class="caps"></q-input>
								</div>
							</div>
							<div class="hris-form-row">
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="initials" label="Company Initials: Ex. JE" :rules="[formRules.required]"
										class="caps"></q-input>
								</div>
							</div>
							<div class="hris-form-row">
								<div class="hris-form-field">
									<q-select outlined dense color="green" v-model="parentCompanyId" :options="parentSelectOptions" label="Parent Company"
										option-label="label" option-value="value" emit-value map-options clearable
										hint="Choose an existing company to make this one a subsidiary. Leave blank to create a tenant root."
										hide-dropdown-icon
									></q-select>
									<template v-if="parentCompanyId">
										<div class="text-caption text-grey q-mt-sm">
											Tenant: {{ parentTenantName || '—' }}
										</div>
									</template>
									<template v-else-if="canChooseTenant">
										<q-select outlined dense color="green" v-model="selectedTenantId" :options="tenantOptions"
											option-label="name" option-value="id" emit-value map-options label="Tenant"
											:rules="[formRules.required]" class="q-mt-sm" @input="syncParentFromTenant"></q-select>
									</template>
									<template v-else>
										<div class="text-caption text-grey q-mt-sm">
											Tenant: {{ currentTenantName || '—' }}
										</div>
									</template>
								</div>
							</div>
							<div class="hris-form-row">
								<div class="hris-form-field">
									<label class="hris-form-label">Status</label>
									<div class="q-gutter-sm">
										<q-radio keep-color color="green" v-model="is_active" val="Y" label="Active" :rules="[formRules.required]"></q-radio>
										<q-radio keep-color color="green" v-model="is_active" val="N" label="In-active"
											:rules="[formRules.required]"></q-radio>
									</div>
								</div>
							</div>
							<div class="hris-form-row">
								<div class="hris-form-field">
									<q-file ref="logoUploader" :key="logoInputKey" v-model="logoFileInput" label="Upload Company Logo"
										color="green" outlined dense accept="image/*" clearable :rules="[logoSizeRule]"
										:max-file-size="maxLogoSize"></q-file>
									<div class="text-caption text-grey q-mt-xs">
										Max file size: 5 MB.
									</div>
									<div v-if="logoUploadError" class="text-negative text-caption q-mt-xs">
										{{ logoUploadError }}
									</div>
									<div v-if="logoPreviewSrc" class="logo-preview q-mt-sm">
										<img :src="logoPreviewSrc" alt="Selected logo">
									</div>
									<div class="text-caption text-grey q-mt-xs">
										<span v-if="selectedStoragePath">Storage folder: {{ selectedStoragePath }}</span>
										<span v-else>Storage folder will be created when the company is saved.</span>
									</div>
								</div>
							</div>
							<div class="hris-form-actions">
								<q-btn unelevated class="hris-q-primary full-width" type="submit" label="Save"></q-btn>
							</div>
						</div>
					</div>
					<div class="col-12 col-md-8">
						<div class="hris-panel">
							<h2 class="hris-panel-title">Company List</h2>
							<q-table
								flat
								:data="data"
								:columns="columns"
								row-key="id"
								:pagination.sync="pagination"
							>
								<template v-slot:body="props">
									<q-tr slot="body" :props="props" @click="selectedRow(props)" style="cursor: pointer;">
										<q-td key="id" :props="props">{{ props.row.counter }}</q-td>
										<q-td key="tenant" :props="props">{{ props.row.tenant_name || '—' }}</q-td>
										<q-td key="parent" :props="props">{{ props.row.parent_name || '—' }}</q-td>
										<q-td key="name" :props="props">{{ props.row.name }}</q-td>
										<q-td key="logo" :props="props">
											<div class="logo-table-cell">
												<img v-if="props.row.logo" :src="logoUrl(props.row.logo)"
													alt="Company logo">
												<span v-else class="text-grey">—</span>
											</div>
										</q-td>
										<q-td key="is_active" :props="props">
											<span class="hris-badge" :class="props.row.is_active == 'Y' ? 'hris-badge-success' : 'hris-badge-danger'">
												{{ props.row.is_active == 'Y' ? 'Active' : 'Inactive' }}
											</span>
										</q-td>
										<q-td key="action" :props="props">
											<q-btn flat round dense class="hris-q-ghost" @click="removeRow(props)">
												<i class="fas fa-trash"></i>
												<q-tooltip anchor="top middle" self="bottom middle" :offset="[10, 10]">
													<strong>Remove</strong>
												</q-tooltip>
											</q-btn>
										</q-td>
									</q-tr>
								</template>
								<template v-slot:no-data="">
									<div class="hris-empty full-width">
										No companies found.
									</div>
								</template>
							</q-table>
						</div>
					</div>
				</div>
			</q-form>

			<q-dialog v-model="spinner" persistent>
				<div class="hris-loading" style="background: #fff; border-radius: 14px; padding: 32px 44px;">
					<q-spinner color="green" size="40px"></q-spinner>
				</div>
			</q-dialog>

			<q-dialog v-model="deleted" persistent>
				<div class="hris-modal">
					<div class="hris-modal-header">
						<h2 class="hris-modal-title">Confirm Deletion</h2>
					</div>
					<div class="hris-modal-body">
						This record will be permanently deleted.
					</div>
					<div class="hris-modal-footer">
						<q-btn flat label="Cancel" class="hris-q-ghost" v-close-popup></q-btn>
						<q-btn unelevated label="Delete" class="hris-q-primary" v-close-popup @click="deletionConfirmation"></q-btn>
					</div>
				</div>
			</q-dialog>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import mixins from '../../mixins';


export default {

	data() {
		return {
			counter: 1,
			tempID: 0,
			spinner: false,
			deleted: false,
			logoFile: null,
			logoPreviewUrl: '',
			logoObjectUrl: null,
			selectedLogoPath: '',
			selectedStoragePath: '',
			logoInputKey: 0,
			logoFileInput: null,
			logoUploadError: '',
			maxLogoSize: 5 * 1024 * 1024,
			company: null,
			// the starting identifier of the company. 
			// example: if the company is "Je310" the initials is "JE" or whatever the company initials you want
			initials: null,
			is_active: 'Y',
			selectedTenantId: null,
			parentCompanyId: null,
			tenantOptions: [],
			data: [],
			columns: [
				{ name: 'id', align: 'center', label: 'ID', field: 'id', sortable: true },
				{ name: 'tenant', align: 'center', label: 'Tenant', field: 'tenant_name', sortable: true },
				{ name: 'parent', align: 'center', label: 'Parent Company', field: 'parent_name', sortable: false },
				{ name: 'name', align: 'center', label: 'Name', field: 'name', sortable: true },
				{ name: 'logo', align: 'center', label: 'Logo', field: 'logo', sortable: false },
				{ name: 'is_active', align: 'center', label: 'Status', field: 'is_active', sortable: true },
				{ name: 'action', align: 'center', label: 'Action', field: '', sortable: true },
			],
			pagination: {
				rowsPerPage: 20
			},
		}
	},

	created() {
		this.loadTenants();
		this.displayData()
	},

	computed: {
		logoPreviewSrc() {
			if (this.logoPreviewUrl) {
				return this.logoPreviewUrl;
			}
			if (this.selectedLogoPath) {
				return this.logoUrl(this.selectedLogoPath);
			}
			return '';
		},
		canChooseTenant() {
			const user = this.userData || {};
			const roleId = Number(user.role_id ?? user.role ?? -1);
			const isSaasOwner = Boolean(user.is_saas_owner ?? user.is_sass_owner);
			return roleId === 0 || isSaasOwner;
		},
		userTenantId() {
			const user = this.userData || {};
			return Number(user.tenant_id ?? user.tenant ?? 0) || null;
		},
		currentTenantName() {
			const currentTenant = this.tenantOptions.find(t => Number(t.id) === Number(this.selectedTenantId));
			return currentTenant ? currentTenant.name : null;
		},
		parentSelectOptions() {
			const excludeId = Number(this.tempID ?? 0);
			return this.data
				.filter(row => Number(row.id) !== excludeId)
				.filter(row => {
					if (!this.canChooseTenant || !this.selectedTenantId) {
						return true;
					}
					return Number(row.tenant_id) === Number(this.selectedTenantId);
				})
				.map((row) => ({
					label: row.tenant_name ? `${row.name} (${row.tenant_name})` : row.name,
					value: Number(row.id),
				}));
		},
		parentTenantName() {
			if (!this.parentCompanyId) {
				return null;
			}
			const parentRow = this.data.find(row => Number(row.id) === Number(this.parentCompanyId));
			return parentRow?.tenant_name ?? null;
		}
	},

	watch: {
		logoFileInput(file) {
			const uploadReady = this.validateLogoSize(file);
			if (!uploadReady) {
				this.resetLogoInput(false);
				return;
			}
			this.updateLogoPreview(file);
		},
		parentCompanyId(newParentId) {
			if (newParentId) {
				this.syncTenantFromParent(newParentId);
			}
		}
	},

	methods: {

		onSubmit: function () {
			this.spinner = true;
			const tenantPayloadId = this.resolveTenantPayloadId();

			if (!tenantPayloadId) {
				this.spinner = false;
				this.$q.notify({
					type: 'negative',
					message: 'Please select a tenant for this company.'
				});
				return;
			}

			let formData = new FormData();

			formData.append("name", this.company);
			formData.append("initials", this.initials.toUpperCase());
			formData.append("status", this.is_active);
			formData.append("tempID", this.tempID);
			formData.append("tenant_id", tenantPayloadId);
			formData.append("parent_id", this.parentCompanyId ?? '');

			let fileToUpload = this.logoFile;

			if (!fileToUpload && this.$refs.logoUploader && this.$refs.logoUploader.files && this.$refs.logoUploader.files.length) {
				fileToUpload = this.$refs.logoUploader.files[0];
			}

			if (fileToUpload && !this.validateLogoSize(fileToUpload)) {
				this.spinner = false;
				return;
			}

			if (fileToUpload) {
				formData.append("logo", fileToUpload, fileToUpload.name);
			}

			const requestConfig = {
				headers: {
					'Authorization': `Bearer ${this.token}`,
				}
			};

			axios.post(`${this.apiUrl}/admin/company`, formData, requestConfig)
				.then((res) => {
					this.displayData()
					this.company = ''
					this.initials = ''
					this.is_active = ''
					this.tempID = 0
					this.parentCompanyId = null;
					this.setDefaultTenantSelection();
					this.spinner = false;
					this.$refs.vForm.reset()
					this.resetLogoInput(true);
					const companyId = this.$store.getters['auth/getUser'].company_id

					if (res.data && res.data.data && companyId == formData.get("tempID")) {
						this.refreshCompanyLogo(res.data.data);
					}
				});
		},

		loadTenants: function () {
			if (!this.canChooseTenant) {
				return
			}

			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).get('/admin/tenants')
				.then((res) => {
					const tenants = res.data.data || [];
					if (this.canChooseTenant) {
						this.tenantOptions = tenants;
					} else {
						const tenantId = this.userTenantId;
						const tenant = tenants.find(item => Number(item.id) === Number(tenantId));
						if (tenant) {
							this.tenantOptions = [tenant];
						} else if (tenants.length) {
							this.tenantOptions = [tenants[0]];
						} else {
							this.tenantOptions = [];
						}
					}
					if (this.canChooseTenant) {
						this.setDefaultTenantSelection();
					} else {
						this.selectedTenantId = this.userTenantId ?? this.tenantOptions[0]?.id ?? null;
					}
				});
		},

		displayData: function () {
			axios.create({
				baseURL: this.apiUrl,
				headers: {
					'Authorization': `Bearer ${this.token}`
				}
			})
				.get(`/admin/company`)
				.then((res) => {
					this.data = res.data.data;
					setTimeout(() => {
						this.spinner = false
					}, 500)
				})

		},

		selectedRow: function (obj) {
			this.resetLogoInput(false);
			this.company = obj.row.name
			this.initials = obj.row.initials
			this.is_active = obj.row.is_active
			this.tempID = obj.row.id
			this.selectedTenantId = obj.row.tenant_id ?? this.userTenantId ?? null
			this.selectedLogoPath = obj.row.logo || ''
			this.selectedStoragePath = obj.row.storage_path || ''
			this.parentCompanyId = obj.row.parent_id ?? null
		},

		removeRow: function (obj) {

			this.deleted = true
			this.tempID = obj.row.id
		},

		syncTenantFromParent(parentId) {
			const parentRow = this.data.find(row => Number(row.id) === Number(parentId));
			if (parentRow && parentRow.tenant_id) {
				this.selectedTenantId = Number(parentRow.tenant_id);
			}
		},

		syncParentFromTenant() {
			if (!this.parentCompanyId || !this.selectedTenantId) {
				return;
			}

			const parentRow = this.data.find(row => Number(row.id) === Number(this.parentCompanyId));
			if (parentRow && Number(parentRow.tenant_id) !== Number(this.selectedTenantId)) {
				this.parentCompanyId = null;
			}
		},

		resolveTenantPayloadId() {
			if (this.parentCompanyId) {
				const parentRow = this.data.find(row => Number(row.id) === Number(this.parentCompanyId));
				return parentRow?.tenant_id ?? this.selectedTenantId ?? this.userTenantId;
			}

			return this.selectedTenantId ?? this.userTenantId;
		},

		setDefaultTenantSelection() {
			if (this.selectedTenantId) {
				return;
			}
			this.selectedTenantId = this.canChooseTenant
				? (this.tenantOptions[0]?.id ?? this.userTenantId)
				: this.userTenantId;
		},

		deletionConfirmation: function () {
			this.spinner = true;
			let formData = new FormData();
			formData.append("tempID", this.tempID);
			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).post('/admin/company/delete', formData)
				.then((res) => {
					this.tempID = 0
					this.displayData()
				});
		},

		resetLogoInput(clearSelection = false) {
			this.clearLogoPreview();
			this.logoFile = null;
			this.logoFileInput = null;
			this.logoInputKey += 1;
			this.logoUploadError = '';
			if (clearSelection) {
				this.selectedLogoPath = '';
				this.selectedStoragePath = '';
				this.selectedTenantId = null;
				this.setDefaultTenantSelection();
			}
		},

		validateLogoSize(file) {
			if (!file) {
				this.logoUploadError = '';
				return true;
			}

			const candidate = file instanceof FileList ? file[0] : (Array.isArray(file) ? file[0] : file);
			if (!candidate || candidate.size <= this.maxLogoSize) {
				this.logoUploadError = '';
				return true;
			}

			this.logoUploadError = 'Logo must be 5 MB or smaller.';
			return false;
		},

		logoSizeRule(value) {
			if (this.validateLogoSize(value)) {
				return true;
			}
			return this.logoUploadError || 'Logo must be 5 MB or smaller.';
		},

		updateLogoPreview(file) {
			if (!file) {
				this.clearLogoPreview();
				this.logoFile = null;
				return;
			}

			this.logoFile = file;
			this.clearLogoPreview();
			this.logoObjectUrl = URL.createObjectURL(file);
			this.logoPreviewUrl = this.logoObjectUrl;
		},

		clearLogoPreview() {
			if (this.logoObjectUrl) {
				URL.revokeObjectURL(this.logoObjectUrl);
			}
			this.logoObjectUrl = null;
			this.logoPreviewUrl = '';
		},

		logoUrl(path) {
			if (!path) {
				return '';
			}
			const normalized = path.replace(/^\//, '');
			return `${this.apiUrl}/${normalized}`;
		},

		refreshCompanyLogo(companyData) {
			if (!companyData) {
				return;
			}
			const currentUser = this.$store.getters['auth/getUser'];
			if (!currentUser) {
				return;
			}

			const updatedUser = {
				...currentUser,
				company_logo_url: companyData.logo_url || currentUser.company_logo_url,
				company_logo_path: companyData.logo_path || currentUser.company_logo_path,
			};
			this.$store.commit('auth/updateUser', updatedUser, { root: true });
		}

	}
}
</script>
<style>
.caps {
	text-transform: uppercase
}

.logo-preview img {
	max-width: 120px;
	max-height: 80px;
	object-fit: contain;
}

.logo-table-cell {
	display: flex;
	justify-content: center;
	align-items: center;
}

.logo-table-cell img {
	max-width: 70px;
	max-height: 40px;
	object-fit: contain;
}
</style>
